import React, { memo, useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  IconButton,
  Link,
  Skeleton,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Switch,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MessageIcon from '@mui/icons-material/Message';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InlineAttendanceConfirm from '@/features/dashboard/components/InlineAttendanceConfirm';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import PreSessionSummaryView from './PreSessionSummaryView';
import MABriefingView from './MABriefingView';
import { ScreeningTrendIcon, formatTime, formatTimeUntil } from './briefingUtils';
import BriefingFooterSections from './BriefingFooterSections';
import ReflectionModal from '@/features/reflections/components/ReflectionModal';
import AdminSnapshotSection from '@/features/analytics/components/AdminSnapshotSection';
import StreakBadge from '@/components/gamification/StreakBadge';
import MicroCelebration from '@/components/gamification/MicroCelebration';
import TemplateSuggestion from '@/components/gamification/TemplateSuggestion';

/**
 * Today's Briefing panel content component.
 * Shows daily briefing information for therapists.
 */
function TodayBriefingPanel({
  data,
  loading,
  onClose,
  onViewPreSession,
  expandedPreSession,
  preSessionData,
  onBackFromPreSession,
  isMobile,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const [reflectionModal, setReflectionModal] = useState({
    open: false,
    type: 'closure',
    impactMessage: null,
  });
  const [showFullNames, setShowFullNames] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    sessions: false,
    billing: false,
    roi: false,
    followup: false,
    forms: false,
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Pages that embed a CalendarPage and can open appointment modals in-place
  const pagesWithCalendar = ['/dashboard', '/admin/dashboard', '/calendar'];
  const hasEmbeddedCalendar = pagesWithCalendar.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));

  const handleClientClick = (link) => {
    // If we're on a page with an embedded calendar and the link targets the
    // calendar with an encounter param, set the param on the current URL so
    // the embedded CalendarPage opens the modal in-place instead of navigating.
    const encounterMatch = link.match(/\/calendar\?encounter=([\w-]+)/);
    if (hasEmbeddedCalendar && encounterMatch) {
      setSearchParams((prev) => {
        prev.set('encounter', encounterMatch[1]);
        return prev;
      }, { replace: true });
      onClose();
      return;
    }
    navigate(link);
    onClose();
  };

  const handleOpenReflection = (type, impactMessage = null) => {
    setReflectionModal({ open: true, type, impactMessage });
  };

  const handleCloseReflection = () => {
    setReflectionModal({ open: false, type: 'closure', impactMessage: null });
  };

  if (loading && !data) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="text" width="60%" height={32} />
        <Skeleton variant="text" width="80%" height={24} sx={{ mt: 1 }} />
        <Skeleton variant="rectangular" height={80} sx={{ mt: 2, borderRadius: 1 }} />
        <Skeleton variant="text" width="40%" height={24} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Unable to load briefing data
        </Typography>
      </Box>
    );
  }

  // If showing pre-session summary
  if (expandedPreSession && preSessionData) {
    return (
      <PreSessionSummaryView
        data={preSessionData}
        onBack={onBackFromPreSession}
        onClose={onClose}
        isMobile={isMobile}
        navigate={navigate}
      />
    );
  }

  if (data.is_ma) {
    return (
      <MABriefingView
        data={data}
        onClose={onClose}
        onViewPreSession={onViewPreSession}
        navigate={navigate}
        onClientClick={handleClientClick}
        isMobile={isMobile}
      />
    );
  }

  const { 
    total_sessions, 
    completed_sessions, 
    all_sessions,
    next_session, 
    has_session_in_next_15_minutes,
    progress_text,
    alerts,
    admin_snapshot,
    screening_summaries,
    // Therapist delight features
    note_celebration,
    end_of_day_ritual,
    done_enough_nudge,
    year_end_reflection,
    recent_cancellations,
    // Phase A delight amplifiers
    documentation_streak,
    milestones,
    template_suggestions,
    // Phase B personality
    personalised_welcome,
  } = data;

  return (
    <Box sx={{ maxHeight: isMobile ? '85vh' : 450, overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: 1,
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        bgcolor: 'background.paper',
        zIndex: 1,
      }}>
        <Typography variant="h6" fontWeight={600}>
          {personalised_welcome || "Today\u0027s Briefing"}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Micro-Celebration Overlay (Phase A) */}
      <MicroCelebration
        milestones={milestones}
        streakMilestone={documentation_streak?.milestone || null}
      />

      {/* Documentation Streak (Phase A) */}
      {documentation_streak && (
        <Box sx={{ mx: 2, mt: 2 }}>
          <StreakBadge streak={documentation_streak} />
        </Box>
      )}

      {/* Note Celebration (4A) - Micro celebration after note signing */}
      {note_celebration && (
        <Box sx={{ 
          p: 1.5, 
          mx: 2, 
          mt: 2,
          bgcolor: 'success.50', 
          borderRadius: 1,
          border: 1,
          borderColor: 'success.100',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon fontSize="small" color="success" />
            <Typography variant="body2">
              {note_celebration.message}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Progress Summary */}
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {progress_text}
        </Typography>
        
        {/* Next Session Inline - Show above sessions chip */}
        {next_session && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Next session with {next_session.client_initials} starts {formatTimeUntil(next_session.starts_in_minutes)}
          </Typography>
        )}
        
        {/* Session Progress Indicator - Clickable to expand */}
        {total_sessions > 0 && (
          <>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              justifyContent: 'space-between',
            }}>
              <Chip
                icon={<CheckCircleIcon fontSize="small" />}
                label={`${completed_sessions}/${total_sessions} sessions`}
                size="small"
                color={completed_sessions === total_sessions ? 'success' : 'default'}
                variant="outlined"
                onClick={() => toggleSection('sessions')}
                onDelete={() => toggleSection('sessions')}
                deleteIcon={expandedSections.sessions ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                sx={{ cursor: 'pointer' }}
              />
              {/* HIPAA name toggle */}
              {expandedSections.sessions && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={showFullNames}
                      onChange={(e) => setShowFullNames(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      {showFullNames ? 'Names' : 'Initials'}
                    </Typography>
                  }
                  sx={{ m: 0, gap: 0.25 }}
                  labelPlacement="start"
                />
              )}
            </Box>
            
            {/* Expandable Sessions List */}
            <Collapse in={expandedSections.sessions} timeout="auto" unmountOnExit>
              <List dense disablePadding sx={{ mt: 1 }}>
                {all_sessions?.map((session) => (
                  <ListItem 
                    key={session.encounter_id} 
                    disablePadding 
                    sx={{ mb: 0.5 }}
                  >
                    <Box sx={{
                      width: '100%',
                      py: 0.5,
                      px: 1,
                      borderRadius: 1,
                      bgcolor: session.status === 'completed' ? 'success.50' : 
                               session.status === 'cancelled' ? 'grey.100' : 'transparent',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ minWidth: 24 }}>
                          {session.status === 'completed' ? (
                            <CheckCircleIcon sx={{ fontSize: 16 }} color="success" />
                          ) : session.status === 'cancelled' ? (
                            <CloseIcon sx={{ fontSize: 16 }} color="disabled" />
                          ) : (
                            <AccessTimeIcon sx={{ fontSize: 16 }} color="primary" />
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
                              {showFullNames && session.client_first_name
                                ? `${session.client_first_name} ${session.client_last_initial || ''}.`.trim()
                                : session.client_initials}
                            </Typography>
                            {screening_summaries?.[session.patient_id] && (
                              <ScreeningTrendIcon summaries={screening_summaries[session.patient_id]} />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {formatTime(session.start_time, session.timezone)}
                            {session.starts_in_minutes !== null && session.status === 'upcoming' && 
                              ` (${formatTimeUntil(session.starts_in_minutes)})`
                            }
                          </Typography>
                        </Box>
                        {/* Action buttons */}
                        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                          {session.patient_id && session.status !== 'cancelled' && (
                            <Button size="small" variant="text" onClick={() => onViewPreSession(session.encounter_id)} sx={{ textTransform: 'none', minWidth: 'auto', fontSize: '0.7rem', px: 0.5 }}>Summary</Button>
                          )}
                          {session.encounter_id && (
                            <IconButton size="small" onClick={() => handleClientClick(`/calendar?encounter=${session.encounter_id}`)} title="Open Appointment" sx={{ p: 0.25 }}>
                              <CalendarTodayIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                      {/* Inline attendance confirmation — only for past/in-progress sessions */}
                      {session.status !== 'cancelled' && session.status !== 'upcoming' && (
                        <Box sx={{ mt: 0.5, ml: 3.5 }}>
                          <InlineAttendanceConfirm session={session} />
                        </Box>
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </Box>

      {/* Next Session - Always show when there is one */}
      {next_session && (
        <>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Next Session
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              p: 1.5,
              bgcolor: has_session_in_next_15_minutes ? 'primary.50' : 'grey.50',
              borderRadius: 1,
              border: 1,
              borderColor: has_session_in_next_15_minutes ? 'primary.200' : 'grey.200',
            }}>
              <ScheduleIcon 
                fontSize="small" 
                color={has_session_in_next_15_minutes ? 'primary' : 'action'} 
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  {next_session.client_initials} at {formatTime(next_session.start_time, next_session.timezone)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatTimeUntil(next_session.starts_in_minutes)}
                </Typography>
              </Box>
              {has_session_in_next_15_minutes && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => onViewPreSession(next_session.encounter_id)}
                  sx={{ textTransform: 'none' }}
                >
                  View summary
                </Button>
              )}
            </Box>
          </Box>
        </>
      )}

      {/* Alerts */}
      {(alerts?.secure_messages?.length > 0 || alerts?.health_tracking?.length > 0) && (
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Updates
          </Typography>
          
          <List dense disablePadding>
            {/* Secure Messages */}
            {alerts.secure_messages?.map((msg, idx) => (
              <ListItem 
                key={`msg-${idx}`}
                disablePadding
                sx={{ mb: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <MessageIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => {
                        navigate(msg.link);
                        onClose();
                      }}
                      sx={{ textAlign: 'left' }}
                    >
                      {msg.client_initials}: {msg.summary}
                    </Link>
                  }
                />
              </ListItem>
            ))}
            
            {/* Health Tracking Alerts */}
            {alerts.health_tracking?.map((alert, idx) => (
              <ListItem 
                key={`health-${idx}`}
                disablePadding
                sx={{ mb: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <TrendingDownIcon fontSize="small" color="warning" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => {
                        navigate(alert.link);
                        onClose();
                      }}
                      sx={{ textAlign: 'left' }}
                    >
                      {alert.client_initials}: {alert.summary}
                    </Link>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* View Full Day link */}
      <Box sx={{ px: 2, py: 1 }}>
        <Button
          size="small"
          variant="text"
          onClick={() => {
            navigate('/dashboard');
            onClose();
          }}
          sx={{ textTransform: 'none', fontSize: '0.8rem', color: 'var(--c1-dark, #2c8db7)' }}
        >
          View full day &rarr;
        </Button>
      </Box>

      {/* Smart Template Suggestions (Phase A) */}
      {template_suggestions?.suggestions?.length > 0 && (
        <Box sx={{ px: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Smart Suggestions
          </Typography>
          <TemplateSuggestion suggestions={template_suggestions.suggestions} />
        </Box>
      )}

      <AdminSnapshotSection
        admin_snapshot={admin_snapshot}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
        handleClientClick={handleClientClick}
      />

      <BriefingFooterSections
        recent_cancellations={recent_cancellations}
        end_of_day_ritual={end_of_day_ritual}
        done_enough_nudge={done_enough_nudge}
        year_end_reflection={year_end_reflection}
        onOpenReflection={handleOpenReflection}
      />

      {/* Empty state for no alerts */}
      {(!alerts?.secure_messages?.length && !alerts?.health_tracking?.length && !next_session) && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No new updates at this time.
          </Typography>
        </Box>
      )}

      {/* Reflection Modal */}
      <ReflectionModal
        open={reflectionModal.open}
        onClose={handleCloseReflection}
        type={reflectionModal.type}
        impactMessage={reflectionModal.impactMessage}
      />
    </Box>
  );
}

export default memo(TodayBriefingPanel);
